import React from 'react'
import { cn } from '@/lib/utils'
import { TILE_TONE, TileTone } from './Tile'

type Props = {
  icon: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
  className?: string
  size?: 'sm' | 'lg'
  /** Tints the icon tile — `danger` for errors, `success` for completed states. */
  tone?: TileTone | 'default'
}

/**
 * Figma empty state: 80px rounded icon tile (canvas fill, 40px glyph),
 * 28px semibold title, 16px muted copy. `sm` is the in-card variant (52px tile).
 */
const EmptyState = ({ icon, title, description, action, className, size = 'lg', tone = 'default' }: Props) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center animate-lc-fade-up',
      className
    )}
  >
    <div
      className={cn(
        'flex items-center justify-center rounded-[16px]',
        tone === 'default'
          ? 'bg-canvas text-ink-2 shadow-[inset_0_0_0_1px_rgb(var(--lc-surface-3))] dark:bg-surface-2'
          : TILE_TONE[tone],
        size === 'lg' ? 'size-20 [&>svg]:size-10' : 'size-[52px] rounded-[12px] [&>svg]:size-6'
      )}
    >
      {icon}
    </div>
    <h3
      className={cn(
        'font-semibold tracking-[-0.3px] text-ink',
        size === 'lg' ? 'mt-8 text-[28px] leading-[1.1]' : 'mt-5 text-[16px] leading-[1.2]'
      )}
    >
      {title}
    </h3>
    {description && (
      <p
        className={cn(
          'text-ink-4 tracking-[-0.2px]',
          size === 'lg' ? 'mt-3 max-w-[320px] text-[16px] leading-[1.2]' : 'mt-1.5 max-w-[240px] text-[14px] leading-[1.4]'
        )}
      >
        {description}
      </p>
    )}
    {action && <div className="mt-5">{action}</div>}
  </div>
)

export default EmptyState
